import React from 'react';
import { z } from 'zod';

import { ColDefBuilder, DataTable, Heading } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { pfshSchema } from '@eluve/llm-outputs';

import { OutputParsingError } from './OutputParsingError';
import { OutputTitle } from './OutputTitle';

export interface PfshOutputProps {
  content: unknown;
}

type MedicationRow = z.infer<typeof pfshSchema>['medications'][number];

const columns = new ColDefBuilder<MedicationRow>()
  .defaultSortable('name')
  .defaultSortable('dose')
  .defaultSortable('frequency')
  .build();

export const PfshOutput: React.FC<PfshOutputProps> = ({ content }) => {
  const { t } = useTranslation();
  const pfsh = pfshSchema.safeParse(content);

  if (!pfsh.success) {
    return <OutputParsingError />;
  }

  return (
    <div className="space-y-4">
      <OutputTitle title={t('appointmentTools.outputs.pfshTitle')} />

      <div className="space-y-2">
        <Heading size="2xl" weight="semibold">
          {t('appointmentTools.outputs.allergies')}
        </Heading>
        <ul>
          {pfsh.data.allergies.map((allergy) => (
            <li key={allergy}>{allergy}</li>
          ))}
        </ul>
      </div>
      <div>
        <Heading size="2xl" weight="semibold">
          {t('appointmentTools.outputs.medications')}
        </Heading>
        <DataTable
          columns={columns}
          data={pfsh.data.medications}
          isPaginated={false}
        />
      </div>
      <div>
        <Heading size="2xl" weight="semibold">
          {t('appointmentTools.outputs.existingConditions')}
        </Heading>
        <ul className="list-inside list-disc">
          {pfsh.data.existingConditions.map((condition) => (
            <li key={condition}>{condition}</li>
          ))}
        </ul>
      </div>
      <div>
        <Heading size="2xl" weight="semibold">
          {t('appointmentTools.outputs.surgeries')}
        </Heading>
        <ul className="list-inside list-disc">
          {pfsh.data.surgeries.map((surgery) => (
            <li key={surgery}>{surgery}</li>
          ))}
        </ul>
      </div>
      <div>
        <Heading size="2xl" weight="semibold">
          {t('appointmentTools.outputs.socialHistory')}
        </Heading>
        <ul className="list-inside list-disc">
          {pfsh.data.socialHistory.map((social) => (
            <li key={social}>{social}</li>
          ))}
        </ul>
      </div>
      <div>
        <Heading size="2xl" weight="semibold">
          {t('appointmentTools.outputs.familyHistory')}
        </Heading>
        <ul className="list-inside list-disc">
          {pfsh.data.familyHistory.map((family) => (
            <li key={family}>{family}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};
